import { Section, SectionHeader } from '../../components/ui/Section'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { Prose } from '../../components/ui/Prose'

const examples = [
  {
    title: 'Incentive Alignment',
    description: 'Designing systems where doing the right thing is also the profitable thing—not through wishful thinking, but through structural incentive design.',
  },
  {
    title: 'Ethical Licensing',
    description: 'IP and licensing models that embed responsibility into the transaction—so downstream use aligns with upstream intentions.',
  },
  {
    title: 'Value Creation Clarity',
    description: 'Distinguishing genuine value creation from value extraction, rent-seeking, and zero-sum games.',
  },
  {
    title: 'Embedded Responsibility',
    description: 'Clear ownership, permissions, conflict rules, audit trails, and review—so responsibility is visible in operations as well as values.',
  },
  {
    title: 'Paid Participation',
    description: 'Combining complementary skills around real work while tracking who contributes, who benefits, and whether opportunity becomes more durable.',
  },
]

export function KindCapitalismPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 md:py-24">
        <div className="container-narrow">
          <span className="inline-block px-3 py-1 text-sm font-medium text-primary-700 bg-primary-100 rounded-full mb-4">
            Applications
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 text-balance">
            Kind Capitalism
          </h1>
          <p className="mt-6 text-xl text-slate-600 leading-relaxed">
            Practical economic design that connects sustainable business,
            responsible incentives, and measurable participation.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <Section narrow className="bg-white pt-0">
        <Prose>
          <h2>Good Intentions Are Not an Operating Model</h2>
          <p>
            Businesses, nonprofits, workers, customers, and communities act
            with different information, constraints, risks, and forms of
            power. A responsible venture has to account for those differences
            in its actual contracts, incentives, permissions, and measurements.
          </p>
          <p>
            Common failures include:
          </p>
          <ul>
            <li>
              describing unpaid exposure as opportunity
            </li>
            <li>
              promising public benefit without measuring who receives it
            </li>
            <li>
              relying on personal trust where governance and written rights are needed
            </li>
            <li>
              separating ethical language from purchasing, compensation, and decision authority
            </li>
          </ul>
          <p>
            <strong>Kind Capitalism</strong> is Sum1 Solutions' provisional
            approach to building ventures where commercial durability and
            public benefit can reinforce one another. It asks whether the
            structure actually:
          </p>
          <ul>
            <li>pays people fairly for useful work</li>
            <li>makes ownership and decision rights explicit</li>
            <li>shares opportunity without disguising private benefit as charity</li>
            <li>tracks outcomes, unwanted effects, and who may have been excluded</li>
            <li>changes when evidence does not support the original story</li>
          </ul>
          <p>
            Its philosophical motivation comes from N=1: participants are not
            isolated abstractions, but perspectives within a shared human and
            economic reality. Their fears, needs, work, and consequences are
            related even when their experiences and power differ. That
            motivation does not determine an economic policy by itself;
            contracts, incentives, distribution, and outcomes still have to be
            examined directly.
          </p>
          <p>
            This is a working design discipline, not a validated economic
            theory. Each venture has to establish its own legal structure,
            economics, safeguards, and evidence.
          </p>
        </Prose>
      </Section>

      {/* Examples */}
      <Section className="bg-slate-50">
        <SectionHeader
          title="Concrete Applications"
          subtitle="Ways to move ethical commitments into operating structure."
        />
        <div className="grid md:grid-cols-2 gap-6">
          {examples.map((example) => (
            <Card key={example.title} hover={false} className="p-6">
              <CardContent className="p-0">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{example.title}</h3>
                <p className="text-slate-600">{example.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* For Different Audiences */}
      <Section narrow className="bg-white">
        <SectionHeader title="Who This Is For" />

        <div className="space-y-8">
          <div className="border-l-4 border-primary-500 pl-6">
            <h3 className="font-semibold text-slate-900 mb-2">For Founders & Entrepreneurs</h3>
            <p className="text-slate-600">
              Building a business that does well by doing good? Kind Capitalism provides frameworks for structuring incentives so sustainability isn't a sacrifice.
            </p>
          </div>

          <div className="border-l-4 border-secondary-500 pl-6">
            <h3 className="font-semibold text-slate-900 mb-2">For Policy Makers</h3>
            <p className="text-slate-600">
              Design programs around how people and organizations actually
              participate, then inspect distribution, access, enforcement, and
              unintended effects.
            </p>
          </div>

          <div className="border-l-4 border-slate-400 pl-6">
            <h3 className="font-semibold text-slate-900 mb-2">For Ethics & Governance</h3>
            <p className="text-slate-600">
              How do you build ethical behavior into systems rather than bolting it on? Kind Capitalism offers structural approaches that go beyond compliance.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 p-8 bg-primary-50 rounded-xl text-center">
          <h3 className="text-xl font-semibold text-slate-900 mb-3">
            Interested in Kind Capitalism?
          </h3>
          <p className="text-slate-600 mb-6">
            Whether you're building a company, a nonprofit program, or a
            collaborative venture, let's turn the values into roles, incentives,
            controls, and outcomes that can be examined.
          </p>
          <Button href="/contact" size="lg">
            Get in Touch
          </Button>
        </div>
      </Section>
    </>
  )
}
